import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  Users,
  CreditCard,
  Utensils,
  Dumbbell,
  Building2,
  UserCog,
  Star,
  Tag,
  MessageSquare,
  QrCode,
  LayoutDashboard,
  Calendar,
  CalendarDays,
  UtensilsCrossed,
  CalendarCheck,
  BookOpen,
  ChevronRight,
  ChevronDown,
  Receipt,
  ShoppingBag,
  BarChart3,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useMobile } from '@/contexts/mobile-context';
import { useUser } from '@/contexts/user-context';
import { useGym } from '@/contexts/gym-context';
import { useView } from '@/contexts/view-context';
import { TenantLogo, TenantLogoSmall } from '@/components/shared/tenant-logo';
import { useChatUnreadCount } from '@/api/chat';

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
}

// Define navigation item type
type NavItem = {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  children?: NavItem[];
};

type NavSection = {
  title?: string;
  items: NavItem[];
};

export function Sidebar({ className, isOpen, ...props }: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile, isSidebarOpen, closeSidebar } = useMobile();
  const { user } = useUser();
  const { gyms } = useGym();
  const { viewMode } = useView();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(['Diet Management', t('sidebar.classes', 'Classes')]));
  const isOwner = user?.userType?.toLowerCase() === 'owner';
  const { data: unreadData } = useChatUnreadCount();

  // Check if user has gyms
  const hasGyms = gyms && gyms.length > 0;

  const handleNavigation = (href: string) => {
    if (isMobile) {
      closeSidebar();
      setTimeout(() => {
        navigate(href);
      }, 100);
    } else {
      navigate(href);
    }
  };

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  };

  const isGroupActive = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some((child) => child.href === pathname);
  };

  // Reusable nav items
  const dashboardItem: NavItem = {
    title: t('sidebar.dashboard'),
    href: '/dashboard',
    icon: LayoutDashboard,
  };

  const classesGroup: NavItem = {
    title: t('sidebar.classes', 'Classes'),
    icon: Calendar,
    children: [
      { title: t('sidebar.manageClasses', 'Manage Classes'), href: '/classes', icon: Calendar },
      { title: t('sidebar.classBookings', 'Bookings'), href: '/class-bookings', icon: CalendarDays },
    ],
  };

  const dietGroupFull: NavItem = {
    title: 'Diet Management',
    icon: Utensils,
    children: [
      { title: 'Diet Plans', href: '/diet-plans-v2', icon: UtensilsCrossed },
      { title: 'Food Library', href: '/food-library', icon: BookOpen },
      { title: 'Diet Calendar', href: '/diet-calendar', icon: CalendarCheck },
    ],
  };

  // Admin navigation, grouped into labeled sections
  const adminSections: NavSection[] = [
    { items: [dashboardItem] },
    ...(hasGyms
      ? [
          {
            title: t('sidebar.sections.members', 'Members'),
            items: [
              { title: t('sidebar.users'), href: '/users', icon: Users },
              { title: t('sidebar.plans'), href: '/plans', icon: CreditCard },
              { title: t('sidebar.payments'), href: '/payments', icon: CreditCard },
              { title: t('sidebar.checkIn'), href: '/check-in', icon: QrCode },
            ],
          },
          {
            title: t('sidebar.sections.operations', 'Operations'),
            items: [
              { title: t('sidebar.gyms'), href: '/gyms', icon: Building2 },
              { title: t('sidebar.staff'), href: '/staff', icon: UserCog },
              classesGroup,
              { title: t('sidebar.exercisePlans'), href: '/exercise-plans', icon: Dumbbell },
              dietGroupFull,
            ],
          },
          {
            title: t('sidebar.sections.catalog', 'Catalog'),
            items: [
              { title: t('sidebar.reviews'), href: '/reviews', icon: Star },
              { title: 'Promotions', href: '/promotions', icon: Tag },
              { title: 'Store', href: '/store', icon: ShoppingBag },
            ],
          },
          {
            title: t('sidebar.sections.insights', 'Insights'),
            items: [
              { title: 'Analytics', href: '/reports', icon: BarChart3 },
              ...(isOwner
                ? [
                    {
                      title: t('sidebar.chat'),
                      href: '/chat',
                      icon: MessageSquare,
                      badge: unreadData?.unreadCount ? String(unreadData.unreadCount) : undefined,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      title: t('sidebar.sections.account', 'Account'),
      items: [{ title: t('sidebar.billing'), href: '/billing', icon: Receipt }],
    },
  ];

  // Reception navigation
  const receptionSections: NavSection[] = [
    { items: [dashboardItem] },
    {
      title: t('sidebar.sections.members', 'Members'),
      items: [
        { title: t('sidebar.users'), href: '/users', icon: Users },
        { title: t('sidebar.payments'), href: '/payments', icon: CreditCard },
      ],
    },
    {
      title: t('sidebar.sections.training', 'Training'),
      items: [
        { title: t('sidebar.exercisePlans'), href: '/exercise-plans', icon: Dumbbell },
        {
          title: 'Diet Management',
          icon: Utensils,
          children: [
            { title: 'Diet Plans', href: '/diet-plans-v2', icon: UtensilsCrossed },
            { title: 'Food Library', href: '/food-library', icon: BookOpen },
          ],
        },
      ],
    },
  ];

  // Trainer navigation
  const trainerSections: NavSection[] = [
    { items: [dashboardItem] },
    {
      title: t('sidebar.sections.schedule', 'Schedule'),
      items: [{ title: t('sidebar.appointments'), href: '/appointments', icon: Calendar }],
    },
    {
      title: t('sidebar.sections.training', 'Training'),
      items: [
        { title: t('sidebar.exercisePlans'), href: '/exercise-plans', icon: Dumbbell },
        {
          title: 'Diet Management',
          icon: Utensils,
          children: [{ title: 'Diet Plans', href: '/diet-plans-v2', icon: UtensilsCrossed }],
        },
      ],
    },
  ];

  // Choose sections based on role
  let sections: NavSection[] = [{ items: [dashboardItem] }];

  if (viewMode === 'admin' || user?.userType?.toLowerCase() === 'owner') {
    sections = adminSections;
  } else if (viewMode === 'reception') {
    sections = receptionSections;
  } else if (viewMode === 'trainer') {
    sections = trainerSections;
  }

  const renderNavItem = (item: NavItem, index: number) => {
    // If item has children, render as a collapsible group
    if (item.children) {
      const isItemOpen = openGroups.has(item.title);
      const isActive = isGroupActive(item);

      return (
        <Collapsible key={`${item.title}-${index}`} open={isItemOpen} onOpenChange={() => toggleGroup(item.title)}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'h-9 w-full justify-start px-3 text-[13px] font-normal',
                isActive
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              {React.createElement(item.icon, { className: 'mr-2.5 h-4 w-4 shrink-0' })}
              <span className="flex-1 text-left">{item.title}</span>
              {isItemOpen ? (
                <ChevronDown className="ml-auto h-3.5 w-3.5 opacity-60" />
              ) : (
                <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-60" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="grid gap-0.5 pt-0.5">
            {item.children.map((child, childIndex) => (
              <Button
                key={`${child.href}-${childIndex}`}
                variant="ghost"
                className={cn(
                  'h-8 w-full justify-start pl-10 pr-3 text-[13px] font-normal',
                  pathname === child.href
                    ? 'bg-accent font-medium text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
                onClick={() => child.href && handleNavigation(child.href)}
              >
                <span className="flex-1 text-left">{child.title}</span>
                {child.badge && (
                  <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-xs">
                    {child.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    // Regular item without children — active state uses a brand accent bar
    return (
      <Button
        key={`${item.href}-${index}`}
        variant="ghost"
        className={cn(
          'relative h-9 w-full justify-start px-3 text-[13px] font-normal',
          pathname === item.href
            ? 'bg-accent font-medium text-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        )}
        onClick={() => item.href && handleNavigation(item.href)}
      >
        {React.createElement(item.icon, { className: 'mr-2.5 h-4 w-4 shrink-0' })}
        <span className="flex-1 text-left">{item.title}</span>
        {item.badge && (
          <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-xs">
            {item.badge}
          </Badge>
        )}
      </Button>
    );
  };

  const navContent = (
    <ScrollArea className="flex-1">
      <nav className="grid gap-0.5 px-3 py-3">
        {sections.map((section, sectionIndex) => (
          <div key={section.title ?? `section-${sectionIndex}`} className="grid gap-0.5">
            {section.title && (
              <p className="px-3 pb-1 pt-4 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {section.title}
              </p>
            )}
            {section.items.map((item, index) => renderNavItem(item, index))}
          </div>
        ))}
      </nav>
    </ScrollArea>
  );

  if (isMobile) {
    return (
      <Sheet open={isSidebarOpen} onOpenChange={closeSidebar}>
        <SheetContent side="left" className="w-[240px] bg-card p-0">
          <div className="flex h-full flex-col">
            <div className="flex h-14 items-center px-4">
              <TenantLogoSmall />
            </div>
            {navContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className={cn('h-full border-r border-border/60 bg-card', className)} {...props}>
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center px-4">
          <TenantLogo />
        </div>
        {navContent}
      </div>
    </div>
  );
}
